import React, { useRef, useState, useEffect } from 'react';

import { convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import moment from 'moment-timezone';
import update from 'immutability-helper';

import API from 'files/api.js';
import Abstract from 'classes/Abstract.js';
import AltFieldMapper, { validateRequiredFields } from 'views/AltFieldMapper.js';
import Appearance from 'styles/Appearance.js';
import Content from 'managers/Content.js';
import FieldMapper, { formatFields } from 'views/FieldMapper.js';
import Layer, { LayerItem } from 'structure/Layer.js';
import Lead from 'classes/Lead.js';
import { LeadDetails } from 'managers/Leads.js';
import LeadScriptEditor from 'views/LeadScriptEditor.js';
import LeadLookupField from 'views/LeadLookupField.js';
import ListField from 'views/ListField.js';
import LottieView from 'views/Lottie.js';
import PageControl from 'views/PageControl.js';
import Panel from 'structure/Panel.js';
import Survey from 'classes/Survey.js';
import QuestionEditor, { QuestionsContainer } from 'views/QuestionEditor.js';
import Request from 'files/Request.js';
import RichTextEditor from 'views/RichTextEditor.js';
import TableListHeader from 'views/TableListHeader.js';
import TextField from 'views/TextField.js';
import User from 'classes/User.js';
import UserLookupField from 'views/UserLookupField.js';
import Utils from 'files/Utils.js';
import Views from 'views/Main.js';

// Panels
export const LeadSurveyEnrollment = ({ index, options, utils }) => {

    const panelID = 'lead_survey_enrollment';
    const [loading, setLoading] = useState(null);
    const [offset, setOffset] = useState(0);
    const [searchOffset, setSearchOffset] = useState(0);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const [entries, setEntries] = useState([]);

    const [sortOrder, setSortOrder] = useState({
        key: 'date',
        forward: false
    });

    const getFields = (entry, index) => {

        let target = entry || {};
        if(Utils.isMobile()) {
            return (
                Views.entry({
                    key: index,
                    title: target.name,
                    subTitle: target.description,
                    hideIcon: true,
                    bottomBorder: true,
                    onClick: onEntryClick.bind(this, entry)
                })
            )
        }

        let fields = [{
            key: 'start_date',
            title: 'Registered',
            value: target.start_date ? (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <span style={{
                        ...Appearance.textStyles.title()
                    }}>{moment(target.start_date).format('MMM Do')}</span>
                    <span style={{
                        ...Appearance.textStyles.subTitle()
                    }}>{moment(target.start_date).format('h:mma')}</span>
                </div>
            ) : null
        },{
            key: 'survey',
            title: 'Survey',
            value: target.survey ? target.survey.name : null
        },{
            key: 'lead',
            title: 'Lead',
            value: target.lead ? target.lead.full_name : null
        },{
            key: 'client',
            title: 'Client',
            visible: utils.user.get().level <= User.levels.get().admin,
            value: target.survey && target.survey.client ? target.survey.client.name : 'All Clients'
        },{
            key: 'end_date',
            title: 'End Date',
            value: target.end_date ? moment(target.end_date).format('MMM Do, YYYY') : 'Open Ended'
        },{
            key: 'status',
            title: 'Status',
            value: getActiveStatus(target)
        }];

        // Headers
        if(!entry) {
            return (
                <tr style={{
                    borderBottom: `1px solid ${Appearance.colors.divider()}`
                }}>
                {fields.map((field, index) => {
                    return (
                        <td
                        key={index}
                        className={'px-3 py-2 flexible-table-column'}>
                            <span style={{
                                ...Appearance.textStyles.title()
                            }}>{field.title}</span>
                        </td>
                    )
                })}
                </tr>
            )
        }

        // Rows
        return (
            <tr
            key={index}
            className={`view-entry ${window.theme}`}
            style={{
                borderBottom: `${index !== entries.length - 1 ? 1 : 0}px solid ${Appearance.colors.divider()}`
            }}>
            {fields.map((field, index) => {
                return (
                    <td
                    key={index}
                    className={'px-3 py-2 flexible-table-column'}
                    onClick={onEntryClick.bind(this, entry)}>
                        <span style={Appearance.textStyles.subTitle()}>{field.value}</span>
                    </td>
                )
            })}
            </tr>
        )
    }

    const getActiveStatus = entry => {
        if(!entry) {
            return;
        }
        let color = entry.active ? Appearance.colors.primary() : Appearance.colors.grey();
        return (
            <div
            className={'text-button'}
            onClick={onChangeActiveStatus.bind(this, entry)}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: 100,
                height: '100%',
                maxWidth: 75,
                textAlign: 'center',
                border: `1px solid ${color}`,
                background: Appearance.colors.softGradient(color),
                borderRadius: 5,
                overflow: 'hidden'
            }}>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    color: 'white',
                    fontWeight: '600',
                    width: '100%'
                }}>{entry.active ? 'Active' : 'Not Active'}</span>
            </div>
        )
    }

    const onChangeActiveStatus = async (entry, e) => {
        try {
            e.stopPropagation();
            await Request.post(utils, '/surveys/', {
                type: 'set_enrollment_active_status',
                id: entry.id,
                active: !entry.active
            });

            setEntries(entries => {
                return entries.map(prevEntry => {
                    if(prevEntry.id === entry.id) {
                        prevEntry.active = !prevEntry.active;
                    }
                    return prevEntry;
                })
            })

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue changing the status of this enrollment. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const getPrintProps = () => {
        return {
            onFetch: onPrintEnrollment,
            onRenderItem: (item, index, items) => ({
                start_date: moment(item.start_date).format('MMMM Do, YYYY [at] h:mma'),
                survey: item.survey.name,
                lead: item.lead.full_name,
                client: item.survey && item.survey.client ? item.survey.client.name : 'All Clients',
                end_date: item.end_date ? moment(item.end_date).format('MMM Do, YYYY') : 'Open Ended',
                status: item.active ? 'Active' : 'Not Active'
            }),
            headers: [{
                key: 'start_date',
                title: 'Registered'
            },{
                key: 'survey',
                title: 'Survey'
            },{
                key: 'lead',
                title: 'Lead'
            },{
                key: 'client',
                title: 'Client',
                visible: utils.user.get().level <= User.levels.get().admin
            },{
                key: 'end_date',
                title: 'End Date'
            },{
                key: 'status',
                title: 'Status'
            }]
        }
    }

    const onPrintEnrollment = async props => {
        return new Promise(async (resolve, reject) => {
            try {
                setLoading(true);
                let { entries } = await Request.get(utils, '/surveys/', {
                    search_text: searchText,
                    type: 'all_lead_enrollment',
                    ...props
                });

                setLoading(false);
                resolve(entries.map(entry => ({
                    ...entry,
                    lead: Lead.create(entry.lead),
                    survey: Survey.create(entry.survey)
                })));

            } catch(e) {
                setLoading(false);
                reject(e);
            }
        })
    }

    const fetchEnrollment = async () => {
        try {
            setLoading(true);
            let { entries, paging } = await Request.get(utils, '/surveys/', {
                type: 'all_lead_enrollment',
                limit: 10,
                offset: offset
            });

            setLoading(false);
            setPaging(paging);
            setSearchOffset(0);
            setEntries(entries.map(entry => ({
                ...entry,
                lead: Lead.create(entry.lead),
                survey: Survey.create(entry.survey)
            })))

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the lead survey enrollment list. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const searchEnrollment = async () => {
        if(!searchText) {
            return;
        }
        try {
            setLoading(true);
            let { entries, paging } = await Request.get(utils, '/surveys/', {
                limit: 10,
                offset: searchOffset,
                search_text: searchText,
                type: 'all_lead_enrollment'
            });

            setLoading(false);
            setOffset(0);
            setPaging(paging);
            setEntries(entries.map(entry => ({
                ...entry,
                lead: Lead.create(entry.lead),
                survey: Survey.create(entry.survey)
            })))

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the lead survey enrollment list. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onEntryClick = entry => {
        utils.sheet.show({
            items: [{
                key: 'survey',
                title: 'View Survey',
                style: 'default'
            },{
                key: 'lead',
                title: 'View Lead',
                style: 'default'
            }]
        }, async key => {
            if(key === 'lead') {
                try {
                    let lead = await Lead.get(utils, entry.lead.id);
                    utils.layer.open({
                        abstract: Abstract.create({
                            object: lead,
                            type: 'leads'
                        }),
                        Component: LeadDetails,
                        id: `lead_details_${entry.lead.id}`,
                        permissions: ['leads.details']
                    });
                } catch(e) {
                    utils.alert.show({
                        title: 'Oops!',
                        message: `There was an issue retrieving the information for this lead. ${e.message || 'An unknown error occurred'}`
                    })
                }
                return;
            }
            if(key === 'survey') {
                try {
                    let survey = await Survey.get(utils, entry.survey.id);
                    utils.layer.open({
                        abstract: Abstract.create({
                            object: survey,
                            type: 'surveys'
                        }),
                        Component: SurveyDetails,
                        id: `survey_details_${entry.survey.id}`,
                        permissions: ['surveys.details']
                    });
                } catch(e) {
                    utils.alert.show({
                        title: 'Oops!',
                        message: `There was an issue retrieving the information for this Survey. ${e.message || 'An unknown error occurred'}`
                    })
                }
                return;
            }
        })
    }

    const getContent = () => {
        if(entries.length === 0) {
            return (
                Views.entry({
                    title: 'No Enrollment Entries Found',
                    subTitle: 'No enrollment entries were found in the system',
                    bottomBorder: false,
                    hideIcon: true
                })
            )
        }
        return (
            <table
            className={'px-3 py-2 m-0'}
            style={{
                width: '100%'
            }}>
                <thead style={{
                    width: '100%'
                }}>
                    {getFields()}
                </thead>
                <tbody style={{
                    width: '100%'
                }}>
                    {entries.map((entry, index) => {
                        return getFields(entry, index)
                    })}
                </tbody>
            </table>
        )
    }

    useEffect(() => {
        fetchEnrollment();
    }, [offset]);

    useEffect(() => {
        searchEnrollment();
    }, [searchOffset]);

    useEffect(() => {
        if(searchText) {
            searchEnrollment();
        } else {
            fetchEnrollment();
        }
    }, [searchText]);

    useEffect(() => {
        utils.events.on(panelID, 'client_change', fetchEnrollment);
        utils.content.subscribe(panelID, ['leads', 'lead_status', 'surveys'], {
            onFetch: fetchEnrollment,
            onUpdate: abstract => {
                setEntries(entries => {
                    return entries.map(entry => {
                        switch(abstract.type) {
                            case 'lead_status':
                            if(entry.lead.id === abstract.getID()) {
                                entry.lead.status = abstract.object.status;
                            }
                            return entry;

                            case 'leads':
                            return entry.lead.id === abstract.getID() ? { ...entry, lead: abstract.object } : entry;

                            case 'surveys':
                            return entry.survey.id === abstract.getID() ? { ...entry, survey: abstract.object } : entry;
                        }
                        return entry;
                    })
                })
            }
        });
        return () => {
            utils.content.unsubscribe(panelID);
            utils.events.off(panelID, 'client_change', fetchEnrollment);
        }
    }, []);

    return (
        <Panel
        index={index}
        title={'Lead Survey Enrollment'}
        utils={utils}
        options={{
            ...options,
            loading: loading,
            search: {
                placeholder: 'Search by Survey or Lead name...',
                onChange: text => setSearchText(text)
            },
            print: getPrintProps(),
            paging: {
                data: paging,
                limit: 10,
                offset: searchText ? searchOffset : offset,
                onClick: nextOffset => {
                    if(searchText) {
                        setSearchOffset(nextOffset);
                        return;
                    }
                    setOffset(nextOffset);
                }
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel()
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const Surveys = ({ id, title }, { index, options, utils }) => {

    const limit = 15;
    const offset = useRef(0);
    const showHidden = useRef(false);
    const sorting = useRef({ sort_key: 'name', sort_type: Content.sorting.type.ascending });

    const [loading, setLoading] = useState(null);
    const [paging, setPaging] = useState(null);
    const [surveys, setSurveys] = useState([]);
    const [searchText, setSearchText] = useState(null);

    const getActiveStatus = survey => {

        if(!survey) {
            return;
        }

        // determine whether or not editing is enabled for this survey
        let { client, level } = utils.user.get();
        let canEdit = level === User.levels.get().admin || (survey.client_id === client.id) ? true : false;

        // return plain text if editing is not enabled for this survey
        if(canEdit === false) {
            return (
                <span>{survey.active ? 'Active' : 'Not Active'}</span>
            )
        }

        // return a badge indicating that they survey is deleted if it is currently hidden
        if(survey.hidden === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    background: Appearance.colors.softGradient(Appearance.colors.red),
                    border: `1px solid ${Appearance.colors.red}`,
                    borderRadius: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    justifyContent: 'center',
                    maxWidth: 75,
                    overflow: 'hidden',
                    textAlign: 'center',
                    width: 100
                }}>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        color: 'white',
                        fontWeight: '600',
                        width: '100%'
                    }}>{'Deleted'}</span>
                </div>
            )
        }

        // return status button with option to change status on click
        let color = survey.active ? Appearance.colors.primary() : Appearance.colors.grey();
        return (
            <div
            {...canEdit && {
                className: 'text-button',
                onClick: onChangeActiveStatus.bind(this, survey)
            }}
            style={{
                alignItems: 'center',
                background: Appearance.colors.softGradient(color),
                border: `1px solid ${color}`,
                borderRadius: 5,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                justifyContent: 'center',
                maxWidth: 75,
                overflow: 'hidden',
                textAlign: 'center',
                width: 100
            }}>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    color: 'white',
                    fontWeight: '600',
                    width: '100%'
                }}>{survey.active ? 'Active' : 'Not Active'}</span>
            </div>
        )
    }

    const onChangeActiveStatus = async (survey, e) => {
        try {
            e.stopPropagation();
            await Request.post(utils, '/surveys/', {
                active: !survey.active,
                id: survey.id,
                type: 'set_active_status'
            });

            setSurveys(surveys => {
                return surveys.map(prevSurvey => {
                    if(prevSurvey.id === survey.id) {
                        prevSurvey.active = !prevSurvey.active;
                    }
                    return prevSurvey;
                });
            });

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue changing the status for this Survey. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getEndDate = survey => {
        if(!survey) {
            return null;
        }
        if(!survey.end_date && !survey.end_days) {
            return 'Open Ended';
        }
        if(survey.end_days) {
            return `${survey.end_days} ${survey.end_days === 1 ? 'day':'days'} after signup`
        }
        let diff = moment(survey.end_date).diff(moment(survey.start_date), 'days');
        if(diff > 0 && diff < 31) {
            return `${diff} ${diff === 1 ? 'day':'days'}`
        }
        return moment(survey.end_date).format('MM/DD/YYYY')
    }

    const onNewSurvey = () => {

        let survey = Survey.new();
        utils.layer.open({
            abstract: Abstract.create({
                object: survey,
                type: 'surveys'
            }),
            Component: AddEditSurvey.bind(this, {
                isNewTarget: true
            }),
            id: 'new_survey',
            permissions: ['surveys.actions.new']
        })
    }

    const onOptionsClick = evt => {
        utils.sheet.show({
            items: [{
                key: 'hidden',
                title: showHidden.current ? 'Hide Deleted Surveys' : 'Show Deleted Surveys',
                style: 'default'
            }],
            position: 'bottom',
            target: evt.target
        }, key => {
            if(key === 'hidden') {
                showHidden.current = !showHidden.current;
                setLoading(true);
                fetchSurveys();
                return;
            }
        });
    }

    const onPrintSurveys = async props => {
        return new Promise(async (resolve, reject) => {
            try {
                setLoading(true);
                let { surveys } = await Request.get(utils, '/surveys/', {
                    type: searchText ? 'lookup' : 'all',
                    search_text: searchText,
                    ...sorting.current,
                    ...props
                });

                setLoading(false);
                resolve(surveys.map(survey => Survey.create(survey)));

            } catch(e) {
                setLoading(false);
                reject(e);
            }
        })
    }

    const onSurveyClick = async id => {
        try {

            // set loading flag and fetch survey details
            setLoading(true);
            let survey = await Survey.get(utils, id);

            // end loading and show survey details layer
            setLoading(false);
            utils.layer.open({
                abstract: Abstract.create({
                    object: survey,
                    type: 'surveys'
                }),
                Component: SurveyDetails,
                id: `survey_details_${id}`,
                permissions: ['surveys.details']
            });
        
        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retreiving the details for this survey. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getButtons = () => {
        return [{
            key: 'options',
            onClick: onOptionsClick,
            style: 'grey',
            title: 'Options',
            visible: utils.user.get().level <= User.levels.get().primary
        },{
            key: 'new',
            onClick: onNewSurvey,
            style: 'default',
            title: `New ${title}`
        }];
    }

    const getContent = () => {
        if(surveys.length === 0) {
            return (
                Views.entry({
                    bottomBorder: false,
                    hideIcon: true,
                    subTitle: 'No surveys were found in the system',
                    title: 'No Surveys Found'
                })
            )
        }
        return (
            <table
            className={'px-3 py-2 m-0'}
            style={{
                width: '100%'
            }}>
                <thead style={{
                    width: '100%'
                }}>
                    {getFields()}
                </thead>
                <tbody style={{
                    width: '100%'
                }}>
                    {surveys.map((survey, index) => {
                        return getFields(survey, index)
                    })}
                </tbody>
            </table>
        )
    }

    const getFields = (survey, index) => {

        let target = survey || {};
        let fields = [{
            key: 'name',
            title: 'Name',
            value: target.name
        },{
            key: 'description',
            title: 'Description',
            value: target.description
        },{
            key: 'end_date',
            title: 'Ends',
            sortable: false,
            value: getEndDate(survey)
        },{
            key: 'registered',
            title: 'Registered',
            value: target.registered || 0
        },{
            key: 'status',
            title: 'Status',
            sortable: false,
            value: getActiveStatus(survey)
        }];

        // create table headers with custom sorting options
        // conform external sort to match internal header sort if applicable
        if(!survey) {
            let sorts = {
                [Content.sorting.type.alphabetically]: 'name',
                [Content.sorting.type.ascending]: 'created',
                [Content.sorting.type.descending]: 'created'
            };
            return (
                <TableListHeader
                fields={fields}
                onChange={props => {
                    sorting.current = props;
                    setLoading(true);
                    fetchSurveys();
                }}
                {...sorting.current && sorting.current.general === true && {
                    value: {
                        key: sorts[sorting.current.sort_type],
                        direction: sorting.current.sort_type
                    }
                }} />
            )
        }

        // loop through result rows
        return (
            <tr
            key={index}
            className={`view-entry ${window.theme}`}
            style={{
                borderBottom: `${index !== surveys.length - 1 ? 1 : 0}px solid ${Appearance.colors.divider()}`
            }}>
            {fields.map((field, index) => {
                return (
                    <td
                    key={index}
                    className={'px-3 py-2 flexible-table-column'}
                    onClick={onSurveyClick.bind(this, survey.id)}>
                        <span style={Appearance.textStyles.subTitle()}>{field.value}</span>
                    </td>
                )
            })}
            </tr>
        )
    }

    const getPrintProps = () => {
        return {
            onFetch: onPrintSurveys,
            onRenderItem: item => ({
                name: item.name,
                description: item.description,
                end_date: getEndDate(item),
                registered: item.registered || 0,
                status: getActiveStatus(item)
            }),
            headers: [{
                key: 'name',
                title: 'Name'
            },{
                key: 'description',
                title: 'Description'
            },{
                key: 'end_date',
                title: 'Ends'
            },{
                key: 'registered',
                title: 'Registered'
            },{
                key: 'status',
                title: 'Status'
            }]
        }
    }

    const fetchSurveys = async () => {
        try {
            setLoading(true);
            let { surveys, paging } = await Request.get(utils, '/surveys/', {
                limit: limit,
                offset: offset.current,
                search_text: searchText,
                show_hidden: showHidden.current,
                type: 'all',
                ...sorting.current
            });

            setLoading(false);
            setPaging(paging);
            setSurveys(surveys.map(survey => Survey.create(survey)))

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the surveys list. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    useEffect(() => {
        fetchSurveys();
    }, [searchText]);

    useEffect(() => {
        utils.events.on(id, 'client_change', fetchSurveys);
        utils.content.subscribe(id, ['surveys'], {
            onFetch: fetchSurveys,
            onUpdate: abstract => {
                setSurveys(surveys => {
                    return surveys.map(survey => {
                        return survey.id === abstract.getID() ? abstract.object : survey
                    })
                })
            }
        });
        return () => {
            utils.content.unsubscribe(id);
            utils.events.off(id, 'client_change', fetchSurveys);
        }
    }, []);

    return (
        <Panel
        index={index}
        title={`${title}s`}
        utils={utils}
        options={{
            ...options,
            buttons: getButtons(),
            loading: loading === true,
            print: getPrintProps(),
            paging: {
                data: paging,
                limit: limit,
                laoding: loading === 'paging',
                offset: offset.current,
                onClick: next => {
                    offset.current = next;
                    setLoading('paging');
                    fetchSurveys();
                }
            },
            search: {
                placeholder: `Search by ${title} name...`,
                onChange: setSearchText
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel()
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const SurveyTemplates = ({ index, options, utils }) => {

    const panelID = 'survey_templates';
    const limit = 15;

    const [loading, setLoading] = useState(null);
    const [offset, setOffset] = useState(0);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const [sorting, setSorting] = useState(null);
    const [templates, setTemplates] = useState([]);

    const getActiveStatus = template => {

        if(!template) {
            return;
        }

        // determine whether or not editing is enabled for this template
        let { client, level } = utils.user.get();
        let canEdit = level === User.levels.get().admin || (template.client_id === client.id) ? true : false;

        // return plain text if editing is not enabled for this survey
        if(canEdit === false) {
            return (
                <span>{template.active ? 'Active' : 'Not Active'}</span>
            )
        }

        let color = template.active ? Appearance.colors.primary() : Appearance.colors.grey();
        return (
            <div
            className={'text-button'}
            onClick={onChangeActiveStatus.bind(this, template)}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: 100,
                height: '100%',
                maxWidth: 75,
                textAlign: 'center',
                border: `1px solid ${color}`,
                background: Appearance.colors.softGradient(color),
                borderRadius: 5,
                overflow: 'hidden'
            }}>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    color: 'white',
                    fontWeight: '600',
                    width: '100%'
                }}>{template.active ? 'Active' : 'Not Active'}</span>
            </div>
        )
    }

    const onChangeActiveStatus = async (template, e) => {
        try {
            e.stopPropagation();
            await Request.post(utils, '/surveys/', {
                type: 'set_template_active_status',
                id: template.id,
                active: !template.active
            });
            setTemplates(templates => {
                return templates.map(prevTemplate => {
                    if(prevTemplate.id === template.id) {
                        prevTemplate.active = !prevTemplate.active;
                    }
                    return prevTemplate;
                })
            })

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue changing the status of this template. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onNewTemplate = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: Survey.Template.new(),
                type: 'templates'
            }),
            Component: AddEditTemplate.bind(this, {
                isNewTarget: true
            }),
            id: 'new_template',
            permissions: ['surveys.templates.actions.new']
        })
    }

    const onPrintTemplates = async props => {
        return new Promise(async (resolve, reject) => {
            try {
                setLoading(true);
                let { templates } = await Request.get(utils, '/surveys/', {
                    type: 'templates',
                    search_text: searchText,
                    ...sorting,
                    ...props
                });

                setLoading(false);
                resolve(templates.map(template => Survey.Template.create(template)));

            } catch(e) {
                setLoading(false);
                reject(e);
            }
        });
    }

    const onTemplateClick = template => {
        utils.layer.open({
            abstract: Abstract.create({
                object: template,
                type: 'templates'
            }),
            Component: TemplateDetails,
            id: `template_details_${template.id}`,
            permissions: ['surveys.templates.details']
        });
    }

    const getContent = () => {
        if(templates.length === 0) {
            return (
                Views.entry({
                    bottomBorder: false,
                    hideIcon: true,
                    subTitle: 'No templates were found in the system',
                    title: 'No Templates Found'
                })
            )
        }
        return (
            <table
            className={'px-3 py-2 m-0'}
            style={{
                width: '100%'
            }}>
                <thead style={{
                    width: '100%'
                }}>
                    {getFields()}
                </thead>
                <tbody style={{
                    width: '100%'
                }}>
                    {templates.map((template, index) => {
                        return getFields(template, index);
                    })}
                </tbody>
            </table>
        )
    }

    const getFields = (template, index) => {

        let target = template || {};
        if(Utils.isMobile()) {
            return (
                Views.entry({
                    key: index,
                    title: target.title,
                    subTitle: target.description,
                    hideIcon: true,
                    bottomBorder: true,
                    onClick: onTemplateClick.bind(this, template)
                })
            )
        }

        let fields = [{
            key: 'title',
            title: 'Title',
            value: target.title
        },{
            key: 'description',
            title: 'Description',
            value: target.description
        },{
            key: 'used_by',
            title: 'Used By',
            value: `${target.used_by} ${target.used_by === 1 ? 'survey':'surveys'}`
        },{
            key: 'questions',
            title: 'Questions',
            value: target.questions ? `${target.questions.length} ${target.questions.length === 1 ? 'question':'questions'}` : null
        },{
            key: 'request_appointment',
            title: 'Request a Appointment',
            value: target.request_appointment ? 'Yes' : 'No'
        },{
            key: 'status',
            title: 'Status',
            sortable: false,
            value: getActiveStatus(target)
        }];

        // create table headers with custom sorting options
        // conform external sort to match internal header sort if applicable
        if(!template) {
            let sorts = {
                [Content.sorting.type.alphabetically]: 'title',
                [Content.sorting.type.ascending]: 'date',
                [Content.sorting.type.descending]: 'date'
            };
            return (
                <TableListHeader
                fields={fields}
                onChange={props => setSorting(props)}
                {...sorting && sorting.general === true && {
                    value: {
                        key: sorts[sorting.sort_type],
                        direction: sorting.sort_type
                    }
                }} />
            )
        }

        // loop through result rows
        return (
            <tr
            key={index}
            className={`view-entry ${window.theme}`}
            style={{
                borderBottom: `${index !== templates.length - 1 ? 1 : 0}px solid ${Appearance.colors.divider()}`
            }}>
            {fields.map((field, index) => {
                return (
                    <td
                    key={index}
                    className={'px-3 py-2 flexible-table-column'}
                    onClick={onTemplateClick.bind(this, template)}>
                        <span style={Appearance.textStyles.subTitle()}>{field.value}</span>
                    </td>
                )
            })}
            </tr>
        )
    }

    const getPrintProps = () => {
        return {
            onFetch: onPrintTemplates,
            onRenderItem: (item, index, items) => {
                return {
                    title: item.title,
                    description: item.description,
                    used_by: `${item.used_by} ${item.used_by === 1 ? 'survey' : 'surveys'}`,
                    questions: item.questions ? `${item.questions.length} ${item.questions.length === 1 ? 'question':'questions'}` : null,
                    request_appointment: item.request_appointment ? 'Yes' : 'No',
                    status: getActiveStatus(item)
                }
            },
            headers: [{
                key: 'title',
                title: 'Title'
            },{
                key: 'description',
                title: 'Description'
            },{
                key: 'used_by',
                title: 'Used By'
            },{
                key: 'questions',
                title: 'Questions'
            },{
                key: 'request_appointment',
                title: 'Request a Appointment'
            },{
                key: 'status',
                title: 'Status'
            }]
        }
    }

    const fetchTemplates = async () => {
        try {
            if(utils.fetching.get(panelID) === true) {
                return;
            }
            setLoading(true);
            utils.fetching.set(panelID, true);
            let { templates, paging } = await Request.get(utils, '/surveys/', {
                type: 'templates',
                limit: limit,
                offset: offset,
                search_text: searchText,
                ...sorting
            });

            setLoading(false);
            utils.fetching.set(panelID, false);

            setPaging(paging);
            setTemplates(templates.map(template => Survey.Template.create(template)))

        } catch(e) {
            setLoading(false);
            utils.fetching.set(panelID, false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the templates list. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    useEffect(() => {
        fetchTemplates();
    }, [offset, searchText, sorting]);

    useEffect(() => {
        utils.events.on(panelID, 'client_change', fetchTemplates);
        utils.content.subscribe(panelID, ['templates'], {
            onFetch: fetchTemplates,
            onUpdate: abstract => {
                setTemplates(templates => {
                    return templates.map(template => {
                        return template.id === abstract.getID() ? abstract.object : template
                    })
                })
            }
        });
        return () => {
            utils.content.unsubscribe(panelID);
            utils.events.off(panelID, 'client_change', fetchTemplates);
        }
    }, []);

    return (
        <Panel
        index={index}
        title={'Templates'}
        utils={utils}
        options={{
            ...options,
            loading: loading,
            search: {
                placeholder: 'Search by template title...',
                onChange: setSearchText
            },
            buttons: [{
                key: 'new',
                title: 'New Template',
                style: 'default',
                onClick: onNewTemplate
            }],
            print: getPrintProps(),
            paging: {
                data: paging,
                limit: limit,
                offset: offset,
                onClick: nextOffset => setOffset(nextOffset)
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel()
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

// Layers
export const AddEditSurvey = ({ isNewTarget, surveyAgreementTemplate }, { abstract, index, options, utils }) => {

    const layerID = isNewTarget ? `new_survey` : `edit_survey_${abstract.getID()}`;
    const [agreementTemplate, setAgreementTemplate] = useState(surveyAgreementTemplate);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [survey, setSurvey] = useState(null);
    const [scripts, setScripts] = useState([]);
    const [templates, setTemplates] = useState([]);

    const onDoneClick = async () => {

        try {
            setLoading('done');
            await Utils.sleep(0.25);
            await validateRequiredFields(getFields);

            // create target
            if(isNewTarget) {
                await abstract.object.submit(utils, { agreement_template_id: agreementTemplate ? agreementTemplate.id : null });
                setLoading(false);
                utils.banner.show({
                    title: 'Survey Created',
                    message: `Your new survey has been created and is ready to use.`,
                    onShow: () => setLayerState('close')
                });
                return;
            }

            // update target
            await abstract.object.update(utils, { agreement_template_id: agreementTemplate ? agreementTemplate.id : null });
            setLoading(false);
            utils.banner.show({
                title: 'Survey Updated',
                message: `This survey has been updated and is ready to use.`,
                onShow: () => setLayerState('close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} this survey. ${e.message || 'An unknown error occurred'}`
            })
        }
        return;
    }

    const onNewTemplate = async () => {
        try {
            utils.layer.requestClose('edit_target');
            await Utils.sleep(0.5);

            utils.layer.open({
                abstract: Abstract.create({
                    object: Survey.Template.new(),
                    type: 'templates'
                }),
                Component: AddEditTemplate.bind(this, {
                    isNewTarget: true
                }),
                id: 'new_template',
                permissions: ['surveys.templates.actions.new']
            })

        } catch(e) {
            console.log(e.messsage);
        }
    }

    const onUpdateTarget = props => {
        let edits = abstract.object.set(props)
        setSurvey(edits);
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'done',
            loading: loading === 'done',
            onClick: onDoneClick,
            text: isNewTarget ? 'Done' : 'Save Changes'
        }];
    }

    const getFields = () => {

        if(!survey) {
            return [];
        }

        let items = [{
            key: 'about',
            title: `About this Survey`,
            items: [{
                component: 'textfield',
                description: `What is the name of this survey?`,
                key: 'name',
                onChange: text => onUpdateTarget({ name: text }),
                title: 'Name',
                value: survey.name
            },{
                component: 'textview',
                description: `What is the description for this survey?`,
                key: 'description',
                onChange: text => onUpdateTarget({ description: text }),
                title: 'Description',
                value: survey.description
            }]
        },{
            key: 'details',
            title: 'Details',
            items: [{
                component: 'list',
                description: 'What is the purpose of this survey? The category determines which fields are shown to the customer when filling out the survey.',
                items: [{
                    id: 'lead_generation',
                    title: 'Lead Generation'
                },{
                    id: 'recruiting',
                    title: 'Recruiting'
                }],
                key: 'category',
                props: {
                    placeholder: 'Choose a category...'
                },
                onChange: item => onUpdateTarget({ category: item ? item.id : null }),
                title: 'Category',
                value: survey.category ? (survey.category === 'recruiting' ? 'Recruiting' : 'Lead Generation') : null,
                visible: survey.allow_sharing === true
            },{
                component: 'client_lookup',
                description: `What client does this survey belong to?`,
                key: 'client',
                onChange: client => onUpdateTarget({ client: client }),
                required: false,
                title: 'Client',
                value: survey.client,
                visible: isNewTarget && utils.user.get().level <= User.levels.get().admin
            },{
                component: 'list',
                description: 'Would you like this survey to have an ending date? If yes, choose the type of end date that you would like to create.',
                items: [{
                    id: 'date',
                    title: 'Specific Date'
                },{
                    id: 'days',
                    title: 'Number of Days After Signup'
                }],
                key: 'end_date_type',
                onChange: item => onUpdateTarget({ end_date_type: item ? item.id : null }),
                props: {
                    placeholder: 'Choose an end date type...'
                },
                required: false,
                title: 'End Date',
                value: survey.end_date_type ? (survey.end_date_type === 'date' ? 'Specific Date' : 'Number of Days After Signup') : null,
                visible: survey.allow_sharing === false
            },{
                component: 'date_picker',
                description: 'Would you like this survey to end on a specific date?',
                key: 'end_date',
                onChange: date => onUpdateTarget({ end_date: date }),
                props: {
                    dateTime: false
                },
                required: false,
                title: 'End on Specific Date',
                value: survey.end_date,
                visible: survey.end_date_type === 'date'
            },{
                component: 'list',
                description: `Would you like this survey to end based on the safety associate's signup date? If yes, then choose the amount of days that the safety associate has to complete this survey`,
                items: [...new Array(365)].map((_, index) => ({
                    code: index + 1,
                    title: `${index + 1} ${(index + 1) === 1 ? 'day':'days'}`
                })),
                key: 'end_days',
                onChange: item => onUpdateTarget({ end_days: item ? item.code : null }),
                props: {
                    placeholder: 'Choose an amount of days...',
                },
                required: false,
                title: 'End a Number of Days After Signup',
                value: survey.end_days ? `${survey.end_days} ${survey.end_days === 1 ? 'day':'days'}` : null,
                visible: survey.end_date_type === 'days' 
            },{
                component: 'textfield',
                description: `If needed, you can set a goal for "Total Appointments Set" that all enrolled users must meet in order to fulfil the requirements of this survey. Setting a goal for appointments is optional.`,
                key: 'appointment_goals',
                onChange: text => {
                    if(!text) {
                        onUpdateTarget({ props: null });
                        return;
                    }
                    if(!survey.props || !survey.props.goals) {
                        onUpdateTarget({
                            props: {
                                goals: {
                                    appointments: parseInt(text)
                                }
                            }
                        });
                        return;
                    }
                    onUpdateTarget({
                        props: update(survey.props, {
                            goals: {
                                appointments: {
                                    $set: parseInt(text)
                                }
                            }
                        })
                    })
                },
                props: {
                    format: 'integer',
                },
                required: false,
                title: 'Appointments Goal',
                value: survey.props && survey.props.goals ? survey.props.goals.appointments : null,
                visible: survey.allow_sharing === false
            },{
                component: 'list',
                description: `What template would you like to use for this survey?`,
                items: getTemplateItems(),
                key: 'template',
                onChange: item => {
                    if(!item) {
                        onUpdateTarget({ template: null });
                        return;
                    }
                    let template = templates.find(temp => temp.id === item.code);
                    onUpdateTarget({ template: template });
                },
                props: {
                    onAddNew: onNewTemplate
                },
                required: survey.allow_sharing,
                title: 'Template',
                value: survey.template ? survey.template.title : null,
                visible: survey.allow_sharing
            },{
                component: 'lead_type_picker',
                description: `Would you like automatically associate a specific lead type with this survey? This will tag all leads generated through this survey with the selected lead type`,
                key: 'lead_type',
                onChange: result => onUpdateTarget({ lead_type: result }),
                required: false,
                title: 'Lead Type',
                value: survey.lead_type ? survey.lead_type.text : null,
                visible: survey.allow_sharing && survey.category === 'lead_generation'
            },{
                component: 'list',
                description: `Would you like to choose a lead script for this survey? All leads generated with this survey will automatically have this lead script attached. We will attach your client workspace default lead script if you do not choose a lead script.`,
                items: scripts,
                key: 'lead_script',
                onChange: lead_script => onUpdateTarget({ lead_script: lead_script }),
                required: false,
                title: 'Lead Script',
                value: survey.lead_script ? survey.lead_script.text : null,
                visible: survey.allow_sharing && survey.category === 'lead_generation' && scripts.length > 0
            }]
        }]

        return items;
    }

    const getTemplateItems = () => {
        return templates.filter(template => {
            return template.category === survey.category
        }).map(template => ({
            code: template.id,
            title: template.title
        }));
    }

    const getTitle = () => {
        if(!isNewTarget) {
            return `Editing ${abstract.getTitle()}`;
        }
        return `New Survey`;
    }

    const fetchEditTargets = async () => {
        try {
            let { scripts, templates } = await Request.get(utils, '/surveys/', {
                type: 'edit_targets'
            });
            setScripts(scripts.map(script => Lead.Script.create(script)))
            setTemplates(templates.map(template => Survey.Template.create(template)));

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue completing setup. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close')
            })
        }
    }

    const setupTarget = () => {
        let edits = abstract.object.open();
        if(isNewTarget) {
            edits = abstract.object.set({ client: utils.client.get() });
        }
        setSurvey(edits);
        fetchEditTargets();
    }

    useEffect(() => {
        setTimeout(setupTarget, 500);
        utils.content.subscribe(layerID, ['surveys', 'templates'], {
            onFetch: type => {
                switch(type) {
                    case 'templates':
                    fetchEditTargets();
                    break;
                }
            }
        })
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={getTitle()}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            <AltFieldMapper
            fields={getFields()} 
            utils={utils}/>
        </Layer>
    )
}

export const AddEditSurveyAgreementTemplate = ({ isNewTarget, onAddTemplate }, { abstract, index, options, utils }) => {

    const layerID = isNewTarget ? `new_survey_agreement_template` : `edit_survey_agreement_template_${abstract.getID()}`;
    const [items, setItems] = useState([]);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [template, setTemplate] = useState(null);

    const onDoneClick = async () => {

        try {
            setLoading('done');
            await Utils.sleep(0.25);
            await validateRequiredFields(getFields);

            // create target
            if(isNewTarget) {
                await abstract.object.submit(utils);
                setLoading(false);
                utils.banner.show({
                    title: 'Agreement Created',
                    message: `Your custom agreement has been created and will be shown whenever an agreement is requested for this survey.`,
                    onShow: () => {
                        setLayerState('close');
                        if(typeof(onAddTemplate) === 'function') {
                            onAddTemplate(abstract.object);
                        }
                    }
                });
                return;
            }

            // update target
            await abstract.object.update(utils);
            setLoading(false);
            utils.banner.show({
                title: 'Agreement Updated',
                message: `Your custom agreement has been updated and will be available the next time an agreement is requested for this survey.`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} this template. ${e.message || 'An unknown error occurred'}`
            })
        }
        return;
    }

    const onFormatProp = entry => {
        let value = template.props[entry.code];
        return {
            key: entry.code,
            required: true,
            title: entry.text,
            description: entry.description,
            value: value,
            component: 'textfield',
            onChange: val => {
                onUpdateTarget({
                    props: update(template.props, {
                        [entry.code]: {
                            $set: val
                        }
                    })
                });
            }
        }
    }

    const onUpdateTarget = props => {
        let edits = abstract.object.set(props);
        setTemplate(edits);
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'done',
            loading: loading === 'done',
            onClick: onDoneClick,
            text: isNewTarget ? 'Done' : 'Save Changes'
        }];
    }

    const getFields = () => {

        if(!template) {
            return [];
        }

        return [{
            key: 'compensation',
            title: 'Compensation',
            items: items.filter(entry => {
                return [ 'super_commission' ].includes(entry.code);
            }).map(onFormatProp)
        },{
            key: 'timeframe',
            title: 'Timeframe',
            items: items.filter(entry => {
                return [ 'max_days', 'payment_days' ].includes(entry.code);
            }).map(onFormatProp)
        },{
            key: 'first',
            title: 'First Super Commission Qualifications',
            items: items.filter(entry => {
                return [ 'super_qualified_appointments', 'super_qualified_sales' ].includes(entry.code);
            }).map(onFormatProp)
        },{
            key: 'bonus',
            title: 'Bonus Qualifications',
            items: items.filter(entry => {
                return [ 'bonus_alt_prize_options', 'bonus_prize_options', 'bonus_qualified_appointments', 'bonus_scheduled_appointments', 'bonus_scheduled_appointments_days' ].includes(entry.code);
            }).map(onFormatProp)
        }];
    }

    const setupTarget = async () => {
        try {

            // fetch options
            let { options } = await Request.get(utils, '/surveys/', {
                type: 'agreement_template_options'
            });
            setItems(options);

            // open editing
            let edits = abstract.object.open();
            if(isNewTarget) {
                edits = abstract.object.set({
                    client: utils.client.get(),
                    props: options.reduce((object, option) => {
                        object[option.code] = option.default_value;
                        return object;
                    }, {})
                });
            }
            setTemplate(edits);

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue completing setup. ${e.message || 'An unknown error occurred'}`,
                onClick: () => setLayerState('close')
            })
        }
    }

    useEffect(() => {
        setupTarget();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={isNewTarget ? 'New Survey Agreement Template' : `Editing "${abstract.getTitle()}"`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            <AltFieldMapper
            fields={getFields()} 
            utils={utils}/>
        </Layer>
    )
}

export const AddEditTemplate = ({ isNewTarget }, { abstract, index, options, utils }) => {

    const layerID = isNewTarget ? `new_template` : `edit_template_${abstract.getID()}`;
    const [category, setCategory] = useState(abstract.object.category);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sorting, setSorting] = useState(null);
    const [template, setTemplate] = useState(null);

    const onDeleteQuestion = (question, evt) => {
        evt.stopPropagation();
        utils.alert.show({
            title: 'Delete Question',
            message: 'Are you sure that you want to delete this question? This can not be undone.',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onUpdateTarget({
                        questions: update(abstract.object.edits.questions, {
                            $apply: questions => questions.filter(prev_question => {
                                return prev_question.id !== question.id;
                            })
                        })
                    });
                    return;
                }
            }
        });
    }

    const onDoneClick = async () => {
        try {

            // verify that required fields have been completed
            setLoading('done');
            await validateRequiredFields(getFields);

            // determine if a new target needs to be created
            if(isNewTarget) {
                await abstract.object.submit(utils);
            } else {
                await abstract.object.update(utils);
            }

            // show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Template Saved',
                message: `Your template has been ${isNewTarget ? 'created' : 'updated'} and is available for use.`,
                onShow: () => {
                    if(isNewTarget) {
                        setLayerState('close');
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} this template. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onEditQuestion = question => {

        // prevent editing for questions that are marked as not-editable
        if(question && question.editable === false) {
            utils.alert.show({
                title: 'Just a Second',
                message: `This question is used to generate one or more reports and can not be modified. ${question.sortable === true ? 'However, you can move the question to a new position in your template if needed' : ''}`
            });
            return;
        }

        // open editing layer for question
        utils.layer.open({
            id: question ? `edit_template_question_${question.id}` : 'new_template_question',
            Component: AddEditTemplateQuestion.bind(this, {
                isNewTarget: question ? false : true,
                onChange: result => {
                    result.editable = true;
                    result.sortable = true;
                    result.order_index = template.questions.length + 1;
                    onUpdateQuestion(result);
                },
                question: question
            })
        });
    }

    const onMoveQuestionClick = (id, value, evt) => {

        // prevent parent click
        evt.stopPropagation();

        // prevent an invalid position from being requested
        let index = template.questions.findIndex(question => question.id === id);
        let val = index + value;
        if(val < 0 || val > template.questions.length - 1) {
            return;
        }

        // update questions order
        let question = template.questions[index];
        let questions = update(template.questions, {
            $splice: [[
                index, 1
            ],[
                val, 0, question
            ]]
        }).map((question, index) => {
            question.order_index = index;
            return question;
        });

        // update local state with order changes
        let edits = abstract.object.set({ questions });
        setTemplate(edits);
    }

    const onSortChange = async ({  newIndex, oldIndex }) => {
        setSorting(null);
        let question = template.questions[oldIndex];
        let edits = abstract.object.set({
            questions: update(template.questions, {
                $splice: [[ oldIndex, 1 ], [ newIndex, 0, question ]]
            }).map((question, index) => {
                question.order_index = index + 1;
                return question;
            })
        });
        setTemplate({ ...edits });
    }

    const onSortStart = ({ index }) => {
        setSorting(index);
    }

    const onUpdateQuestion = question => {
        onUpdateTarget({
            questions: update(abstract.object.edits.questions, {
                $apply: questions => questions.filter(prev_question => {
                    return prev_question.id !== question.id;
                }),
                $push: [ question ]
            })
        })
    }

    const onUpdateTarget = props => {
        let edits = abstract.object.set(props)
        setTemplate(edits);
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'done',
            loading: loading === 'done',
            onClick: onDoneClick,
            text: isNewTarget ? 'Done' : 'Save Changes'
        }];
    }

    const getContent = () => {
        if(!template) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 15
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 50,
                        width: 50
                    }}/>
                </div>
            )
        }
        return (
            <>
            <AltFieldMapper
            fields={getFields()} 
            utils={utils}/>

            <LayerItem
            title={'Questions'}
            style={{
                marginBottom: 35
            }}>
                {getQuestionsContainer()}
            </LayerItem>

            <AltFieldMapper
            fields={getCustomizations()} 
            utils={utils}/>
            </>
        )
    }

    const getCustomizations = () => {

        if(!template) {
            return [];
        }
        return [{
            key: 'customize',
            title: 'Customize',
            items: [{
                key: 'background_image',
                required: false,
                title: 'Background Image',
                description: 'Would you like to add an image to the background of this template? Images are shown fullscreen and must be at least 1920 pixels high by 3072 pixels wide. This ensures that your Survey does not have a blurry background when viewed on larger displays.',
                value: template.props.background_image,
                component: 'image_picker',
                props: {
                    requirements: {
                        dimensions: {
                            width: 3072,
                            height: 1920
                        }
                    }
                },
                onChange: image => {
                    onUpdateTarget({
                        props: update(template.props, {
                            background_image: {
                                $set: image
                            }
                        })
                    })
                }
            },{
                key: 'pattern',
                required: false,
                title: 'Background Pattern',
                description: 'Would you like to add a pattern to the background of this template? This pattern will appear over your background image if you have added a background image.',
                value: template.props ? template.props.pattern : null,
                component: 'pattern_picker',
                onChange: pattern => {
                    onUpdateTarget({
                        props: update(template.props, {
                            pattern: {
                                $set: pattern
                            }
                        })
                    })
                },
                visible: false
            },{
                key: 'header_image',
                required: false,
                title: 'Hero Image',
                description: 'Would you like to add an image at the top of your template? Images must be at least 1024 pixels wide by 512 pixels high. We will show your client name, city, and state if no image is added.',
                value: template.props.header_image,
                component: 'image_picker',
                props: {
                    requirements: {
                        dimensions: {
                            width: 1024,
                            height: 512
                        }
                    }
                },
                onChange: image => {
                    onUpdateTarget({
                        props: update(template.props, {
                            header_image: {
                                $set: image
                            }
                        })
                    })
                }
            },{
                key: 'request_home_address',
                required: false,
                title: 'Request Home Address',
                description: 'When enabled, the template will include a field where a customer can input their full address. This includes street address, city, state, and zip code. When disabled, no address field will be shown on the template.',
                value: typeof(template.props.request_home_address) === 'boolean' ? template.props.request_home_address : true,
                component: 'bool_list',
                onChange: val => {
                    onUpdateTarget({
                        props: update(template.props, {
                            request_home_address: {
                                $set: val
                            }
                        })
                    });
                }
            },{
                key: 'og_image',
                required: false,
                title: 'Social Media Image',
                description: 'Would you like to show a custom image when this Survey is shared on social media? Images must be at least 627 pixels high by 1200 pixels wide. We will show the LifeWipe logo if no image is selected.',
                value: template.props.og_image,
                component: 'image_picker',
                props: {
                    requirements: {
                        dimensions: {
                            width: 1200,
                            height: 627
                        }
                    }
                },
                onChange: image => {
                    onUpdateTarget({
                        props: update(template.props, {
                            og_image: {
                                $set: image
                            }
                        })
                    })
                }
            },{
                key: 'welcome_message',
                required: false,
                title: 'Welcome Message',
                description: 'Would you like your welcome message to say? This message is shown at the top of your template.',
                value: template.props.welcome_message,
                component: 'textview',
                onChange: text => {
                    onUpdateTarget({
                        props: update(template.props, {
                            welcome_message: {
                                $set: text
                            }
                        })
                    });
                }
            }]
        }]
    }

    const getFields = () => {
        if(!template) {
            return [];
        }
        return [{
            key: 'details',
            lastItem: false,
            title: 'Details',
            items: [{
                component: 'list',
                description: 'What is the purpose of this template? The category determines which fields are shown to the customer when filling out a survey where this template is set.',
                items: [{
                    id: 'lead_generation',
                    title: 'Lead Generation'
                },{
                    id: 'recruiting',
                    title: 'Recruiting'
                }],
                key: 'category',
                props: {
                    placeholder: 'Choose a category...'
                },
                onChange: item => onUpdateTarget({ category: item ? item.id : null }),
                title: 'Category',
                value: template.category ? (template.category === 'recruiting' ? 'Recruiting' : 'Lead Generation') : null
            },{
                component: 'client_lookup',
                description: 'Which client do you want to have access to this template? Leaving this blank will allow all clients to use this template.',
                key: 'client',
                onChange: client => onUpdateTarget({ client: client }),
                required: false,
                title: 'Client',
                value: template.client,
                visible: isNewTarget === true && utils.user.get().level <= User.levels.get().admin
            },{
                component: 'textfield',
                description: 'What is the title of this template? This title will appear when a customer visits a survey using this template',
                key: 'title',
                onChange: text => onUpdateTarget({ title: text }),
                required: true,
                title: 'Title',
                value: template.title
            },{
                component: 'textview',
                description: 'What is the description for this template? This description will not be shown to a customer when they visit a survey using this template',
                key: 'description',
                onChange: text => onUpdateTarget({ description: text }),
                required: true,
                title: 'Description',
                value: template.description
            },{
                component: 'bool_list',
                description: 'Should we give the customer an option to request a appointment?',
                key: 'request_appointment',
                onChange: enabled => onUpdateTarget({ request_appointment: enabled }),
                required: false,
                title: 'Request a Appointment',
                value: template.request_appointment,
                visible: template.category === 'lead_generation'
            }]
        }];
    }

    const getQuestions = () => {
        if(!template || !template.questions) {
            return [];
        }
        return template.questions.sort((a, b) => {
            return a.order_index - b.order_index;
        });
    }

    const getQuestionComponent = (question, index, questions) => {
        let { editable, sortable, title, type } = question;
        let locked = editable === false && sortable === false;
        return (
            <div
            {...type !== Survey.Template.Question.type.file_picker && {
                className: `view-entry ${window.theme}`,
                onClick: onEditQuestion.bind(this, question)
            }}
            key={index}
            style={{
                alignItems: 'center',
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                display: 'flex',
                flexDirection: 'row',
                minWidth: 0,
                padding: '8px 12px 8px 12px',
                width: '100%',
            }}>
                {locked && (
                    <img
                    src={'images/lock-icon-small.png'}
                    style={{
                        height: 20,
                        marginRight: 4,
                        objectFit: 'contain',
                        width: 20
                    }} />
                )}
                {Views.entry({
                    bottomBorder: false,
                    hideIcon: true,
                    subTitle: `${Survey.Template.Question.input.getLabel(question)}: ${Survey.Template.Question.input.getDescription(question)}`,
                    title: title
                })}
                <img
                className={'text-button'}
                src={'images/up-arrow-grey-small.png'}
                title={'Move Up'}
                onClick={onMoveQuestionClick.bind(this, question.id, -1)}
                style={{
                    height: 20,
                    marginLeft: 8,
                    minWidth: 20,
                    objectFit: 'contain',
                    opacity: index === 0 ? 0.25 : 1,
                    overflow: 'hidden',
                    width: 20
                }} />
                <img
                className={'text-button'}
                src={'images/down-arrow-grey-small.png'}
                title={'Move Down'}
                onClick={onMoveQuestionClick.bind(this, question.id, 1)}
                style={{
                    height: 20,
                    marginLeft: 2,
                    minWidth: 20,
                    objectFit: 'contain',
                    opacity: index === questions.length - 1 ? 0.25 : 1,
                    overflow: 'hidden',
                    width: 20
                }} />
                <img
                className={'text-button'}
                src={'images/red-x-icon.png'}
                onClick={onDeleteQuestion.bind(this, question)}
                style={{
                    height: 20,
                    marginLeft: 8,
                    objectFit: 'contain',
                    width: 20
                }} />
            </div>
        )
    }

    const getQuestionsContainer = () => {
        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                marginBottom: 20
            }}>
                <QuestionsContainer
                axis={'y'}
                lockAxis={'y'}
                pressDelay={200}
                onSortStart={onSortStart}
                onSortEnd={onSortChange}
                helperClass={'sortable-container'}
                questions={getQuestions()}
                onRenderQuestion={getQuestionComponent}/>

                <div
                className={'text-button'}
                onClick={onEditQuestion.bind(this, null)}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    padding: 12,
                    textAlign: 'center',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.title(),
                        color: Appearance.colors.primary()
                    }}>{'Add a New Question'}</span>
                </div>
            </div>
        )
    }

    const fetchDefaultQuestions = async () => {
        try {

            // send request to server
            setLoading(true);
            let { questions } = await Request.get(utils, '/surveys/', {
                category: category,
                type: 'default_template_questions'
            });

            // update template edits with default questions
            let edits = abstract.object.set({
                client: utils.client.get(),
                questions: questions.map(question => {
                    return Survey.Template.Question.create(question);
                })
            });

            // update local state with default questions
            setLoading(false);
            setTemplate(edits);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the default questions for this template category. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close')
            });
        }
    }

    const setupTarget = () => {
        let edits = abstract.object.open();
        setTemplate(edits); 
    }

    useEffect(() => {
        if(category && isNewTarget === true) {
            fetchDefaultQuestions();
        }
    }, [category]);

    useEffect(() => {
        setCategory(template && template.category);
    }, [template]);

    useEffect(() => {
        setTimeout(setupTarget, 500);
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={isNewTarget ? `New Template` : `Editing ${abstract.getTitle()}`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading == true
        }}>
            {getContent()}
        </Layer>
    )
}

const AddEditTemplateQuestion = ({ isNewTarget, onChange, question }, { index, options, utils }) => {

    const layerID = isNewTarget ? 'new_template_question' : `edit_template_question_${question.id}`;
    const [layerState, setLayerState] = useState(null);
    const [result, setResult] = useState(question);

    const onDoneClick = () => {

        // close layer and notify subscribers of data change
        setLayerState('close');
        if(typeof(onChange) === 'function') {
            onChange(result);
        }
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'done',
            onClick: onDoneClick,
            text: 'Done'
        }];
    }

    return (
        <Layer 
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={question ? `Editing "${question.title}"` : 'New Question'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            sizing: 'medium'
        }}>
            <QuestionEditor
            onAdd={setResult} 
            onClose={setLayerState.bind(this, 'close')}
            onUpdate={setResult}
            utils={utils}
            value={question} />
        </Layer>
    )
}

export const EnrollSurveyLead = ({ abstract, index, options, utils }) => {

    const layerID = `enroll_survey_lead_${abstract.getID()}`;
    const [loading, setLoading] = useState(false);
    const [layerState, setLayerState] = useState(null);
    const [lead, setLead] = useState(null);

    const onEnrollSurvey = async () => {
        if(!lead) {
            utils.alert.show({
                title: 'Just a Second',
                message: `Please choose a Lead before moving on`
            });
            return;
        }

        try {
            setLoading(true);
            await Utils.sleep(0.25);

            await Request.post(utils, '/surveys/', {
                type: 'enroll_survey_lead',
                id: lead.id,
                survey_id: abstract.getID()
            });

            setLoading(false);
            utils.content.fetch('surveys');

            utils.banner.show({
                title: 'Lead Enrolled',
                message: `${lead.full_name} has been enrolled in the Survey "${abstract.object.name}"`,
                onShow: () => setLayerState('close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue enrolling ${lead.full_name} in this Survey. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'done',
            onClick: onEnrollSurvey,
            text: 'Enroll'
        }];
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={'Survey Enrollment'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            <LeadLookupField
            icon={'search'}
            onChange={setLead}
            placeholder={'Search for a Lead...'}
            utils={utils}/>
        </Layer>
    )
}

export const SurveyPrivacyEditor = ({ content, preview, onChange }, { abstract, index, options, utils }) => {

    const layerID = abstract ? `survey_privacy_editor_${abstract.getID()}` : 'new_survey_privacy_editor';
    const [contentState, setContentState] = useState(null);
    const [previewContent, setPreviewContent] = useState(null);
    const [layerState, setLayerState] = useState(null);

    const onContentChange = state => {
        setContentState(state);
    }

    const onDoneClick = () => {
        if(!contentState) {
            setLayerState('close');
            return;
        }
        try {
            let result = convertToRaw(contentState.getCurrentContent());
            setLayerState('close');
            if(typeof(onChange) === 'function') {
                onChange(result);
            }

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue preparing your content. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const getButtons = () => {
        if(preview) {
            return null;
        }
        return [{
            key: 'done',
            text: 'Done',
            color: 'primary',
            onClick: onDoneClick
        }]
    }

    const getContent = () => {
        if(preview === true) {
            return (
                <div dangerouslySetInnerHTML={{ __html: previewContent }} />
            )
        }
        return (
            <RichTextEditor
            utils={utils}
            content={content}
            onChange={onContentChange}/>
        )
    }

    const setupPreviewContent = () => {
        if(preview !== true || !content) {
            return;
        }
        try {
            let markup = draftToHtml(content);
            setPreviewContent(markup);
        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue preparing your content. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    useEffect(() => {
        setupPreviewContent();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={'Survey Terms and Conditions Editor'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState
        }}>
            {getContent()}
        </Layer>
    )
}

export const SurveyAgreementTemplateDetails = ({ abstract, index, options, utils }) => {

    const layerID = `survey_agreement_template_details_${abstract.getID()}`;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [url, setURL] = useState(null);

    const onEditClick = () => {
        if(!abstract.object.client && utils.user.get().level > User.levels.get().admin) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'This Template is available to everyone in the LifeWipe network and can only be modified by the support. Please create a new Template if you need to change the information in this Template.'
            });
            return;
        }
        utils.layer.open({
            id: `edit_survey_agreement_template_${abstract.getID()}`,
            abstract: abstract,
            Component: AddEditSurveyAgreementTemplate.bind(this, {
                isNewTarget: false
            })
        })
    }

    const onFormatProp = entry => {
        let value = abstract.object.props[entry.code];
        if(value !== null && value !== undefined) {
            if(entry.code.includes('commission')) {
                value = Utils.toCurrency(value, 'USD');

            } else if(entry.code.includes('days')) {
                value = `${Utils.softNumberFormat(value)} ${parseInt(value) === 1 ? 'Day' : 'Days'}`

            } else if(!isNaN(value)) {
                value = Utils.softNumberFormat(value);
            }
        }
        return {
            key: entry.code,
            title: entry.text,
            value: value
        }
    }

    const getButtons = () => {
        return [{
            key: 'edit',
            text: 'Edit',
            color: 'primary',
            onClick: onEditClick
        }];
    }

    const getFields = () => {

        let template = abstract.object;
        let items = [{
            key: 'details',
            title: 'Details',
            items: [{
                key: 'id',
                title: 'ID',
                value: template.id
            },{
                key: 'title',
                title: 'Title',
                value: template.title
            },{
                key: 'date',
                title: 'Created',
                value: template.date ? Utils.formatDate(template.date) : null
            },{
                key: 'client',
                title: 'Client',
                visible: utils.user.get().level <= User.levels.get().admin,
                value: template.client ? template.client.name : null
            }]
        },{
            key: 'compensation',
            title: 'Compensation',
            items: template.options.filter(entry => {
                return [ 'super_commission', 'ultra_commission' ].includes(entry.code);
            }).map(onFormatProp)
        },{
            key: 'timeframe',
            title: 'Timeframe',
            items: template.options.filter(entry => {
                return [ 'max_days', 'payment_days' ].includes(entry.code);
            }).map(onFormatProp)
        },{
            key: 'first',
            title: 'First Super Commission Qualifications',
            items: template.options.filter(entry => {
                return [ 'first_super_qualified_appointments', 'first_super_qualified_sales' ].includes(entry.code);
            }).map(onFormatProp)
        },{
            key: 'second',
            title: 'Second Super Commission Qualifications',
            items: template.options.filter(entry => {
                return [ 'second_super_qualified_appointments', 'second_super_qualified_sales' ].includes(entry.code);
            }).map(onFormatProp)
        },{
            key: 'ultra',
            title: 'Ultra Commission Qualifications',
            items: template.options.filter(entry => {
                return [ 'ultra_qualified_appointments', 'ultra_qualified_sales' ].includes(entry.code);
            }).map(onFormatProp)
        },{
            key: 'bonus',
            title: 'Bonus Qualifications',
            items: template.options.filter(entry => {
                return [ 'bonus_alt_prize_options', 'bonus_prize_options', 'bonus_qualified_appointments', 'bonus_scheduled_appointments', 'bonus_scheduled_appointments_days' ].includes(entry.code);
            }).map(onFormatProp)
        }];

        return items;
    }

    useEffect(() => {
        setURL(`${API.server}/privacy/agreements/index.html?id=${abstract.getID()}&client_id=${utils.client.get().id}&nonce=${moment().unix()}&preview=1`);
        utils.content.subscribe(layerID, ['survey_agreement_templates'], {
            onUpdate: _abstract => {
                if(_abstract.getTag() === abstract.getTag()) {
                    setURL(`${API.server}/privacy/agreements/index.html?id=${abstract.getID()}&client_id=${utils.client.get().id}&nonce=${moment().unix()}&preview=1`);
                }
            }
        })
        return () => {
            utils.content.unsubscribe(layerID);
        }
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={abstract.getTitle()}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                marginBottom: 20
            }}>
                <div style={{
                    borderBottom: `1px solid ${Appearance.colors.divider()}`,
                    height: 350
                }}>
                    <iframe
                    className={'template-preview'}
                    src={url}
                    style={{
                        border: 'none',
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        height: 350,
                        width: '100%'
                    }} />
                </div>
                <div style={{
                    padding: 12,
                    textAlign: 'center'
                }}>
                    <span
                    className={'text-button'}
                    onClick={() => window.open(url)}
                    style={{
                        ...Appearance.textStyles.title(),
                        color: Appearance.colors.primary()
                    }}>{'Open Preview in New Window'}</span>
                </div>
            </div>
            <FieldMapper 
            sections={getFields()} 
            utils={utils}/>
        </Layer>
    )
}

export const SurveyDetails = ({ abstract, index, options, utils }) => {

    const layerID = `survey_details_${abstract.getID()}`;
    const urlLimit = useRef(5);

    const [interactionsCount, setInteractionsCount] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [survey, setSurvey] = useState(abstract.object);
    const [urls, setUrls] = useState([]);
    const [urlOffset, setUrlOffset] = useState(0);
    const [urlPaging, setUrlPaging] = useState(null);

    const onChangeActiveStatus = () => {
        utils.alert.show({
            title: `${abstract.object.active ? 'Deactivate' : 'Activate'} Survey`,
            message: `Are you sure that you want to set "${abstract.object.title}" as ${abstract.object.active ? 'inactive' : 'active'}?`,
            buttons: [{
                key: 'confirm',
                title: `Set as ${abstract.object.active ? 'Inactive' : 'Active'}`,
                style: abstract.object.active ? 'destructive' : 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: abstract.object.active ? 'default' : 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onChangeActiveStatusConfirm();
                }
            }
        });
    }

    const onChangeActiveStatusConfirm = async () => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);

            await Request.post(utils, '/surveys/', {
                type: 'set_active_status',
                id: abstract.getID(),
                active: !abstract.object.active
            });

            abstract.object.active = !abstract.object.active;
            setSurvey(abstract.object);
            utils.content.update(abstract);

            setLoading(false);
            utils.banner.show({
                title: 'Status Updated',
                message: `This Survey has been set as ${abstract.object.active ? 'active' : 'inactive'}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue changing the status of this Survey. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onChangeUrlActiveStatus = url => {
        utils.alert.show({
            title: `${url.active ? 'Deactivate' : 'Activate'} URL`,
            message: `Are you sure that you want to ${url.active ? 'deactivate' : 'activate'} this url?`,
            buttons: [{
                key: 'confirm',
                title: `${url.active ? 'Deactivate' : 'Activate'}`,
                style: url.active ? 'destructive' : 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: url.active ? 'default' : 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onChangeUrlActiveStatusConfirm(url);
                    return;
                }
            }
        });
    }

    const onChangeUrlActiveStatusConfirm = async url => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);

            let status = !url.active;
            await Request.post(utils, '/surveys/', {
                type: 'set_url_active_status',
                id: url.id,
                active: status
            });

            setUrls(urls => {
                return urls.map(prev_url => {
                    if(prev_url.id === url.id) {
                        prev_url.active = status;
                    }
                    return prev_url;
                })
            });

            setLoading(false);
            utils.banner.show({
                title: 'URL Updated',
                message: `This url has been ${status ? 'activated' : 'deactivated'}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue changing the status of this url. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onCopySurveyURL = async url => {

        let textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.top = 0;
        textArea.style.left = 0;
        textArea.style.position = 'fixed';
        textArea.style.opacity = 0;
        document.body.appendChild(textArea);

        textArea.focus();
        textArea.select();

        try {
            let copied = document.execCommand('copy');
            document.body.removeChild(textArea);
            if(!copied) {
                throw new Error('Your browser denied the copy request');
            }

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue copying your Survey link. ${(typeof(e) === 'string' ? e : e.message) || 'An unknown error occurred'}`
            })
        }
    }

    const onDownloadSurveyQRCode = async url => {
        try {
            let link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.download = 'qr_code.png';
            document.body.appendChild(link);
            link.click();

            await Utils.sleep(0.25);
            document.body.removeChild(link);

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue downloading your Survey QR Code. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onEditClick = () => {

        // prevent moving forward if survey is global and user is not an administrator
        if(!abstract.object.client_id && utils.user.get().level > User.levels.get().admin) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'This Survey is available to everyone in the LifeWipe network and can only be modified by the support. Please create a new survey if you need to change the information in this Survey.'
            });
            return;
        }

        // open layer to edit survey
        utils.layer.open({
            abstract: abstract,
            Component: AddEditSurvey.bind(this, {
                isNewTarget: false
            }),
            id: `edit_survey_${abstract.getID()}`,
            permissions: ['surveys.actions.edit']
        });
    }

    const onGenerateURL = () => {
        utils.sheet.show({
            title: 'Generate a Public URL',
            message: `What type of url are you needing? A general purpose url can be used across your entire client. Survey kiosk mode automatically refreshes the survey after a submission. A survey credit url is meant to give specific lead credit each time a new lead is generated using the url.`,
            items: [{
                key: 'general',
                title: 'General Purpose',
                style: 'default'
            },{
                key: 'kiosk',
                title: 'Survey Kiosk Mode',
                style: 'default'
            },{
                key: 'enrollment_credit',
                title: 'URL with Survey Credit',
                style: 'default'
            }]
        }, key => {
            if(key === 'general') {
                onGenerateURLProps();
                return;
            }
            if(key === 'kiosk'){
                onGenerateURLConfirm({ kiosk: true });
                return;
            }
            if(key === 'enrollment_credit'){
                onGenerateURLProps({ enroll_user: true });
                return;
            }
        });
    }

    const onGenerateURLProps = props => {

        let { enroll_user } = props || {};

        // prepare variables for domain name selection, enrollment user, and custom url label
        let domain = 'gdl-link.com';
        let user = null;
        let value = null;

        // show alert requesting custom url values
        utils.alert.show({
            title: enroll_user ? 'URL with Survey Credit' : 'Public URL',
            message: `${enroll_user ? 'Generating a url with survey credit allows us to give the proper credit when a Lead signs up using the url. ' : ''}All urls are automatically shortened for quicker sharing but can be easily customized with your own label. A gdl-link.com url with a custom label would look like https://gdl-link.com/your-label-here. We'll generate a label for you if you don't feel like creating your own.`,
            content: (
                <div style={{
                    padding: 12,
                    width: '100%'
                }}>
                    <ListField 
                    disablePlaceholder={true}
                    items={getDomainNameItems()}
                    onChange={item => domain = item.id} 
                    placeholder={'Domain Name'}
                    style={{
                        marginBottom: 8
                    }}
                    value={domain}/>
                    {enroll_user && (
                        <UserLookupField
                        containerStyle={{
                            marginBottom: 8
                        }}
                        icon={'none'}
                        onChange={result => user = result}
                        placeholder={'Assign Credit To...'} 
                        utils={utils}/>
                    )}
                    <TextField
                    onChange={text => value = text}
                    placeholder={'Custom Label (optional)'}/>
                </div>
            ),
            buttons: [{
                key: 'done',
                title: 'Create URL',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: async key => {
                if(key === 'done') {
                    if(enroll_user && !user) {
                        return;
                    }
                    onGenerateURLConfirm({
                        ...props,
                        domain: domain,
                        short_name: value,
                        user: user
                    });
                }
            }
        });
    }

    const onGenerateURLConfirm = async ({ domain, kiosk, short_name, user }) => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);
            let { qr_code, url } = await Request.get(utils, '/surveys/', {
                domain: domain,
                enrollment_user_id: user && user.user_id,
                kiosk: kiosk,
                survey_id: abstract.getID(),
                short_name: short_name,
                type: 'public_url'
            });

            setLoading(false);
            fetchURLs();

            utils.alert.show({
                title: 'URL Generated',
                message: `We have created ${user ? `the survey credit url for ${user.full_name}` : 'general-use url that can be used across your client workspace'}. You can scan the QR code below to open the survey or you can copy the url and share it manually. ${kiosk ? 'This survey will automaticaly reset after each submission so the next customer can fill it out.' : ''}`,
                content: (
                    <div style={{
                        padding: 12,
                        paddingTop: 0
                    }}>
                        <img
                        src={qr_code}
                        style={{
                            width: 250,
                            height: 250,
                            objectFit: 'contain',
                            borderRadius: 10,
                            overflow: 'hidden'
                        }} />
                    </div>
                ),
                buttons: [{
                    key: 'copy',
                    title: 'Copy URL',
                    style: 'default'
                },{
                    key: 'download',
                    title: 'Download QR Code',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'copy') {
                        onCopySurveyURL(url);
                        return;
                    }
                    if(key === 'download') {
                        onDownloadSurveyQRCode(qr_code);
                        return;
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue creating your link for ${user ? user.full_name : 'this Survey'}. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onLeadScriptClick = () => {
        utils.layer.open({
            Component: LeadScriptEditor.bind(this, {
                utils: utils,
                value: survey.lead_script.text
            }),
            id: 'lead_script_editor',
            permissions: ['leads.scripts.actions.edit']
        });
    }

    const onOptionsClick = evt => {

        let { client, level } = utils.user.get();
        let canEdit = level === User.levels.get().admin || survey.client_id === client.id ? true : false;
        
        utils.sheet.show({
            items: [{
                key: 'generate_url',
                permissions: ['surveys.surveys.actions.url.new'],
                title: 'Generate a Public URL',
                style: 'default',
                visible: survey.allow_sharing === true
            },{
                key: 'remove',
                title: survey.hidden ? 'Add Back to Client' : 'Remove from Client',
                style: survey.hidden ? 'default' : 'destructive',
                visible: survey.client_id && survey.allow_sharing === true && utils.user.get().level <= User.levels.get().primary
            },{
                key: 'active',
                permissions: survey.allow_sharing ? ['surveys.surveys.actions.status'] : ['surveys.actions.status'],
                title: `${abstract.object.active ? 'Deactivate' : 'Activate'} Survey`,
                style: abstract.object.active ? 'destructive' : 'default',
                visible: canEdit
            }],
            target: evt.target
        }, key => {
            if(key === 'active') {
                onChangeActiveStatus();
                return;
            }
            if(key === 'generate_url') {
                setTimeout(onGenerateURL, 500);
                return;
            }
            if(key === 'enroll_lead') {
                utils.layer.open({
                    id: `enroll_survey_lead_${abstract.getID()}`,
                    abstract: abstract,
                    Component: EnrollSurveyLead
                })
                return;
            }
            if(key === 'remove') {
                onSetHiddenStatus();
                return;
            }
        });
    }

    const onPreviewQRCode = ({ qr_code, url }) => {
        utils.alert.show({
            title: 'QR Code Preview',
            message: `You can scan the QR Code below to open the Survey or you can copy the url and share it manually`,
            content: (
                <div style={{
                    padding: 12,
                    paddingTop: 0
                }}>
                    <img
                    src={qr_code}
                    style={{
                        width: 250,
                        height: 250,
                        objectFit: 'contain',
                        borderRadius: 10,
                        overflow: 'hidden'
                    }} />
                </div>
            ),
            buttons: [{
                key: 'copy',
                title: 'Copy URL',
                style: 'default'
            },{
                key: 'download',
                title: 'Download QR Code',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'copy') {
                    onCopySurveyURL(url);
                    return;
                }
                if(key === 'download') {
                    onDownloadSurveyQRCode(qr_code);
                    return;
                }
            }
        });
    }

    const onPrivacyClick = () => {
        utils.layer.open({
            id: `survey_privacy_editor_${abstract.getID()}`,
            abstract: abstract,
            Component: SurveyPrivacyEditor.bind(this, {
                preview: true,
                content: abstract.object.privacy
            })
        });
    }

    const onSetHiddenStatus = () => {
        utils.alert.show({
            title: abstract.object.hidden ? 'Add Back to Client Workspace' : 'Remove from Client Workspace',
            message: `Are you sure that you want to ${abstract.object.hidden ? 'add this survey back to your client workspace' : 'remove this survey from your client workspace'}?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: abstract.object.hidden ? 'default' : 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: abstract.object.hidden ? 'cancel' : 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onSetHiddenStatusConfirm();
                    return;
                }
            }
        });
    }

    const onSetHiddenStatusConfirm = async () => {
        try {
            setLoading('options');
            await Request.post(utils, '/surveys/', {
                hidden: !abstract.object.hidden,
                type: 'set_hidden_status',
                id: abstract.getID(),
            });

            // update hidde flag for abstract target
            abstract.object.hidden = !abstract.object.hidden;
            setSurvey(abstract.object);

            // notify subscribers that a content fetch is required
            utils.content.fetch('surveys');

            setLoading(false);
            utils.banner.show({
                title: 'Visibility Updated',
                message: `This survey has been ${abstract.object.hidden ? 'removed from your client workspace' : 'added back to your client workspace'}.`,
                onShow: abstract.object.hidden && setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue changing the status of this url. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onTemplateClick = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: survey.template,
                type: 'templates'
            }),
            Component: TemplateDetails,
            id: `template_details_${survey.template.id}`,
            permissions: ['surveys.templates.details']
        });
    }

    const onUrlClick = url => {
        utils.sheet.show({
            items: [{
                key: 'set_status',
                permissions: ['surveys.surveys.actions.url.status'],
                title: url.active ? 'Deactivate' : 'Activate',
                style: url.active ? 'destructive' : 'default'
            },{
                key: 'copy',
                title: 'Copy URL',
                style: 'default'
            },{
                key: 'qr_code',
                title: 'View QR Code',
                style: 'default',
                visible: url.qr_code ? true : false
            }]
        }, key => {
            if(key === 'set_status') {
                onChangeUrlActiveStatus(url);
                return;
            }
            if(key === 'copy') {
                onCopySurveyURL(url.short_url);
                return;
            }
            if(key === 'qr_code') {
                onPreviewQRCode(url);
                return;
            }
        })
    }

    const getButtons = () => {
        return [{
            color: 'secondary',
            key: 'options',
            loading: loading === 'options',
            onClick: onOptionsClick,
            text: 'Options'
        },{
            color: 'primary',
            key: 'edit',
            onClick: onEditClick,
            permissions: survey.allow_sharing ? ['surveys.surveys.actions.edit'] : ['surveys.actions.edit'],
            text: 'Edit'
        }];
    }

    const getDomainNameItems = () => {

        // prepare default list of items
        let items = [{
            id: 'gdl-link.com',
            title: 'gdl-link.com'
        },{
            id: 'survey-box.com',
            title: 'survey-box.com'
        },{
            id: 'surveydropbox.com',
            title: 'surveydropbox.com'
        }];

        // add replicated website domain name if applicable
        let { replicated_website } = utils.client.get().preferences || {};
        if(replicated_website && replicated_website.url) {
            items.push({
                id: 'replicated_website_url',
                title: replicated_website.url.replace('https://', '')
            });
        }

        // return list of alphabetically sorted domain name items
        return items.sort((a,b) => {
            return a.title.localeCompare(b.title);
        });
    }

    const getFields = () => {

        if(!survey) {
            return null;
        }

        // prepare field mapper items
        let items = [{
            key: 'about',
            permissions: survey.allow_sharing ? ['surveys.surveys.details.general'] : ['surveys.details.general'],
            title: `About this ${survey.allow_sharing ? 'Survey' : 'Survey'}`,
            items: [{
                key: 'category',
                title: 'Category',
                value: survey.category === 'recruiting' ? 'Recruiting' : 'Lead Generation'
            },{
                key: 'description',
                title: 'Description',
                value: survey.description || 'No description provided'
            },{
                key: 'id',
                title: 'ID',
                value: survey.id
            },{
                key: 'name',
                title: 'Name',
                value: survey.name
            },{
                key: 'template',
                onClick: onTemplateClick,
                title: 'Template',
                value: survey.template ? survey.template.title : null,
            }]
        },{
            key: 'details',
            permissions: survey.allow_sharing ? ['surveys.surveys.details.ext']: ['surveys.details.ext'],
            title: 'Details',
            items: [{
                key: 'client',
                title: 'Client',
                visible: utils.user.get().level <= User.levels.get().admin,
                value: survey.client ? survey.client.name : null
            },{
                key: 'appointment_goals',
                title: 'Appointments Goal',
                visible: survey.props && survey.props.goals && survey.props.goals.appointments ? true : false,
                value: survey.props && survey.props.goals && survey.props.goals.appointments ? `${survey.props.goals.appointments} ${survey.props.goals.appointments === 1 ? 'Appointment':'Appointments'}` : null
            },{
                key: 'end_date',
                title: 'End Date',
                visible: survey.end_date ? true : false,
                value: survey.end_date ? moment(survey.end_date).format('MMMM Do, YYYY [at] h:mma') : null
            },{
                key: 'end_days',
                title: 'End Date',
                visible: survey.end_days ? true : false,
                value: survey.end_days ? `${survey.end_days} ${survey.end_days === 1 ? 'day':'days'} from signup` : null
            },{
                key: 'interactions',
                visible: survey.allow_sharing,
                title: 'Interactions',
                value: interactionsCount === null ? 'Loading...' : (interactionsCount || '0')
            },{
                key: 'lead_script',
                visible: survey.allow_sharing ? true : false,
                title: 'Lead Script',
                value: survey.lead_script ? survey.lead_script.title : null,
                onClick: survey.lead_script ? onLeadScriptClick : null
            },{
                key: 'lead_type',
                visible: survey.allow_sharing ? true : false,
                title: 'Lead Type',
                value: survey.lead_type ? survey.lead_type.text : null
            },{
                key: 'template',
                visible: survey.allow_sharing,
                title: 'Template',
                value: survey.template ? survey.template.title : null
            },{
                key: 'url',
                onClick: onGenerateURL,
                value: 'Click to Create',
                visible: survey.allow_sharing,
                title: 'URL'
            }]
        }];

        // prevent moving forward if no valid items were provided
        let count = formatFields(utils, items).length;
        if(count === 0) {
            return null;
        }

        return (
            <FieldMapper 
            sections={items}  
            utils={utils} />
        )
    }

    const getPreview = () => {
        return (
            <LayerItem
            collapsed={false}
            title={'Content Preview'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <div style={{
                        borderBottom: `1px solid ${Appearance.colors.divider()}`,
                        height: 350
                    }}>
                        <iframe
                        className={'template-preview'}
                        src={getURL()}
                        style={{
                            border: 'none',
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                            height: 350,
                            width: '100%'
                        }} />
                    </div>
                    <div style={{
                        padding: 12,
                        textAlign: 'center'
                    }}>
                        <span
                        className={'text-button'}
                        onClick={() => window.open(getURL())}
                        style={{
                            ...Appearance.textStyles.title(),
                            color: Appearance.colors.primary()
                        }}>{'Open Preview in New Window'}</span>
                    </div>
                </div>
            </LayerItem>
        )
    }

    const getURL = () => {
        return `${API.server}/leads/index.php?id=${abstract.getID()}&preview=1&client_id=${utils.client.get().id}&enrollment_user_id=${utils.user.get().user_id}&theme=${window.theme}`;
    }

    const getUrls = () => {

        // determine if user has permission to view this content
        if(utils.user.permissions.get('surveys.surveys.details.urls') === false) {
            return null;
        }
        
        return urls && urls.length > 0 && (
            <LayerItem title={'Public URLs'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel()
                }}>
                    {urls.map((url, index) => {
                        return (
                            Views.entry({
                                badge: {
                                    text: url.active ? null : 'Deactivated',
                                    color: Appearance.colors.grey()
                                },
                                bottomBorder: true,
                                icon: {
                                    path: 'images/short_url_icon.png'
                                },
                                key: index,
                                onClick: onUrlClick.bind(this, url),
                                subTitle: `Created ${Utils.formatDate(url.date)}`,
                                title: url.token
                            })
                        )
                    })}
                    {urlPaging && (
                        <PageControl
                        data={urlPaging}
                        limit={urlLimit.current}
                        offset={urlOffset}
                        onClick={setUrlOffset}/>
                    )}
                </div>
            </LayerItem>
        )
    }

    const fetchDetails = async () => {
        try {
            let { interactions_count } = await Request.get(utils, '/surveys/', {
                id: abstract.getID(),
                type: 'ext_details'
            });

            setLoading(false);
            setInteractionsCount(interactions_count);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the interactions count for this survey. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const fetchURLs = async () => {
        try {
            let { paging, urls } = await Request.get(utils, '/surveys/', {
                limit: urlLimit.current,
                offset: urlOffset,
                survey_id: abstract.getID(),
                type: 'public_urls'
            });
            setLoading(false);
            setUrls(urls || []);
            setUrlPaging(paging);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the public urls for this survey. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchURLs();
    }, [urlOffset]);

    useEffect(() => {
        fetchDetails();
        utils.content.subscribe(layerID, ['surveys'], {
            onUpdate: _abstract => {
                setSurvey(survey => {
                    return survey.id === _abstract.getID() ? _abstract.object : survey;
                });
            }
        });
        return () => {
            utils.content.unsubscribe(layerID);
        }
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={abstract.getTitle()}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            {getPreview()}
            {getFields()}
            {getUrls()}
        </Layer>
    )
}

export const TemplateDetails = ({ abstract, index, options, utils }) => {

    const layerID = `template_details_${abstract.getID()}`;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [url, setURL] = useState(null);

    const onChangeActiveStatus = () => {
        utils.alert.show({
            title: `Change to ${abstract.object.active ? 'Inactive' : 'Active'}`,
            message: `Are you sure that you want to set "${abstract.object.title}" as ${abstract.object.active ? 'inactive' : 'active'}?`,
            buttons: [{
                key: 'confirm',
                title: `Set as ${abstract.object.active ? 'Inactive' : 'Active'}`,
                style: abstract.object.active ? 'destructive' : 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: abstract.object.active ? 'default' : 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onChangeActiveStatusConfirm();
                }
            }
        });
    }

    const onChangeActiveStatusConfirm = async () => {
        try {
            setLoading('options');
            await Utils.sleep(0.25);

            let next_status = !abstract.object.active;
            await Request.post(utils, '/surveys/', {
                type: 'set_template_active_status',
                active: next_status,
                id: abstract.getID()
            });

            abstract.object.active = next_status;
            utils.content.update(abstract);

            setLoading(false);
            utils.banner.show({
                title: 'Template Updated',
                message: `This template has been set as ${abstract.object.active ? 'active' : 'inactive'}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the active status for this template. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onDeleteTemplate = () => {
        utils.alert.show({
            title: 'Delete Template',
            message: `Are you sure that you want to delete the "${abstract.object.title}" template? This action cannot be undone.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onDeleteTemplateConfirm();
                    return;
                }
            }
        });
    }

    const onDeleteTemplateConfirm = async () => {
        try {

            // set loading flag and submit request to server
            setLoading('options');
            await Request.post(utils, '/surveys/', {
                id: abstract.getID(),
                type: 'delete_template'
            });

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Template Deleted',
                message: 'This template has been deleted from your client workspace.',
                onShow: () => {
                    utils.content.fetch('templates');
                    setLayerState('close');
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue deleting this template. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onEditClick = () => {

        // prevent moving forward if survey is global and user is not an administrator
        if(!abstract.object.client_id && utils.user.get().level > User.levels.get().admin) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'This template is available to everyone in the LifeWipe network and can only be modified by the support. Please create a new template if you need to change the information in this template.'
            });
            return;
        }

        // open template editing layer
        utils.layer.open({
            abstract: abstract,
            Component: AddEditTemplate.bind(this, {
                isNewTarget: false
            }),
            id: `edit_template_${abstract.getID()}`,
            permissions: ['surveys.templates.actions.edit']
        });
    }

    const onOptionsClick = evt => {
        utils.sheet.show({
            items: [{
                key: 'active',
                permissions: ['surveys.templates.actions.status'],
                title: `Change to ${abstract.object.active ? 'Inactive' : 'Active'}`,
                style: abstract.object.active ? 'destructive' : 'default'
            },{
                key: 'delete',
                title: `Delete Template`,
                style: 'destructive'
            }],
            target: evt.target
        }, key => {
            if(key === 'active') {
                onChangeActiveStatus();
                return;
            }
            if(key === 'delete') {
                onDeleteTemplate();
                return;
            }
        });
    }

    const getButtons = () => {
        return [{
            color: 'secondary',
            key: 'options',
            loading: loading === 'options',
            onClick: onOptionsClick,
            text: 'Options'
        },{
            color: 'primary',
            key: 'edit',
            onClick: onEditClick,
            permissions: ['surveys.templates.actions.edit'],
            text: 'Edit'
        }];
    }

    const getFields = () => {

        let template = abstract.object;
        let items = [{
            key: 'details',
            title: 'Details',
            items: [{
                key: 'category',
                title: 'Category',
                value: template.category === 'recruiting' ? 'Recruiting' : 'Lead Generation'
            },{
                key: 'client',
                title: 'Client',
                visible: utils.user.get().level <= User.levels.get().admin,
                value: template.client ? template.client.name : null
            },{
                key: 'description',
                title: 'Description',
                value: template.description
            },{
                key: 'id',
                title: 'ID',
                value: template.id
            },{
                key: 'used_by',
                title: 'In-Use By',
                value: `${template.used_by} ${template.used_by === 1 ? 'survey' : 'surveys'}`
            },{
                key: 'request_appointment',
                title: 'Request a Appointment',
                value: template.request_appointment ? 'Yes' : 'No'
            },{
                color: template.active ? Appearance.colors.green : Appearance.colors.red,
                key: 'active',
                title: 'Status',
                value: template.active ? 'Active' : 'Inactive'
            },{
                key: 'title',
                title: 'Title',
                value: template.title
            }]
        }]

        return items;
    }

    const getURL = () => {
        return `${API.server}/leads/index.php?template_id=${abstract.getID()}&client_id=${utils.client.get().id}&preview=1&v=${moment().unix()}`;
    }

    useEffect(() => {
        setURL(getURL());
        utils.content.subscribe(layerID, ['templates'], {
            onFetch: () => {
                setURL(getURL());
            },
            onUpdate: () => {
                setURL(getURL());
            }
        });
        return () => {
            utils.content.unsubscribe(layerID);
        }
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={abstract.getTitle()}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                marginBottom: 20
            }}>
                <div style={{
                    borderBottom: `1px solid ${Appearance.colors.divider()}`,
                    height: 350
                }}>
                    <iframe
                    className={'template-preview custom-scrollbars'}
                    src={url}
                    style={{
                        border: 'none',
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        height: 350,
                        width: '100%'
                    }} />
                </div>
                <div style={{
                    padding: 12,
                    textAlign: 'center'
                }}>
                    <span
                    className={'text-button'}
                    onClick={() => window.open(`${API.server}/leads/index.php?template_id=${abstract.getID()}&preview=1`)}
                    style={{
                        ...Appearance.textStyles.title(),
                        color: Appearance.colors.primary()
                    }}>{'Open Preview in New Window'}</span>
                </div>
            </div>
            <FieldMapper 
            sections={getFields()} 
            utils={utils} />
        </Layer>
    )
}

export const TemplateQuestionLibrary = ({ onChooseQuestion, onRemoveQuestion }, { abstract, index, options, utils }) => {

    const layerID = `template_question_library_${abstract.getID()}`;
    const limit = 5;

    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [offset, setOffset] = useState(0);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const [selected, setSelected] = useState(null);
    const [questions, setQuestions] = useState([]);

    const onDeleteQuestion = (question, evt) => {
        evt.stopPropagation();
        utils.alert.show({
            title: 'Delete Question',
            message: 'Are you sure that you want to delete this question? This can not be undone.',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onDeleteQuestionConfirm(question);
                    return;
                }
            }
        })
    }

    const onDeleteQuestionConfirm = async question => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);
            await Request.post(utils, '/clients/', {
                type: 'delete_template_question',
                id: question.id
            });

            setLoading(false);
            if(typeof(onRemoveQuestion) === 'function') {
                onRemoveQuestion(question);
            }

            fetchQuestions();
            utils.banner.show({
                title: 'Question Deleted',
                message: `The "${question.title}" question has been deleted.`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue deleting this question. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onDoneClick = () => {
        setLayerState('close');
        if(typeof(onChooseQuestion) === 'function') {
            onChooseQuestion(selected);
        }
    }

    const onQuestionClick = question => {
        setSelected(question);
    }

    const getBadges = question => {
        let badges = [{
            text: Survey.Template.Question.input.getLabel(question),
            color: Appearance.colors.grey()
        }];
        if(selected && selected.id === question.id) {
            badges.push({
                text: 'Selected',
                color: Appearance.colors.primary()
            })
        }
        return badges;
    }

    const getButtons = () => {
        if(!selected) {
            return null;
        }
        return [{
            key: 'done',
            text: 'Done',
            color: 'primary',
            onClick: onDoneClick
        }]
    }

    const fetchQuestions = async () => {
        try {
            let { paging, questions } = await Request.get(utils, '/clients/', {
                type: 'template_questions',
                client_id: utils.client.get().id,
                offset: offset,
                limit: limit,
                search_text: searchText
            });

            setLoading(false);
            if(!searchText && questions.length === 0) {
                utils.alert.show({
                    title: 'Just a Second',
                    message: 'No questions have been added. Questions will be available here after at least one Survey has been created.',
                    onClick: () => setLayerState('close')
                });
                return;
            }
            setPaging(paging);
            setQuestions(questions.map(question => Survey.Template.Question.create(question)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading your library of questions. ${e.message || 'An unknown error occurred'}`,
                onClick: () => setLayerState('close')
            })
        }
    }

    useEffect(() => {
        fetchQuestions();
    }, [offset, searchText]);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={'Template Questions Library'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading == true,
            removePadding: true,
            sizing: 'medium'
        }}>
            <div style={{
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                padding: 15
            }}>
                <TextField
                icon={'search'}
                onChange={setSearchText} 
                placeholder={'Search by question title...'}/>
            </div>
            <div style={{
                padding: 15
            }}>
                <div style={{
                    ...Appearance.styles.unstyledPanel()
                }}>
                    {questions.map((question, index) => {
                        return (
                            Views.entry({
                                key: index,
                                title: question.title,
                                subTitle: Survey.Template.Question.input.getDescription(question),
                                badge: getBadges(question),
                                hideIcon: true,
                                firstItem: index === 0,
                                singleItem: questions.length === 0,
                                lastItem: index === questions.length - 1,
                                bottomBorder: index !== questions.length - 1,
                                onClick: onQuestionClick.bind(this, question),
                                rightContent: (
                                    <img
                                    className={'text-button'}
                                    src={'images/red-x-icon.png'}
                                    onClick={onDeleteQuestion.bind(this, question)}
                                    style={{
                                        width: 20,
                                        height: 20,
                                        objectFit: 'contain',
                                        marginLeft: 8
                                    }} />
                                )
                            })
                        )
                    })}
                </div>
                <PageControl
                data={paging}
                limit={limit}
                offset={offset}
                onClick={next => {
                    setLoading(true);
                    setOffset(next);
                }} />
            </div>
        </Layer>
    )
}
